// "Can't find it" in Inventory › Requests (truck-stock parity 3, Brook's special requests): what
// technicians asked for that they couldn't find on their truck, oldest first; the office answers got
// (a note the tech reads, say when it'll be on the truck) or not got, saying why. Answering moves no
// stock: a transfer or a PO does that.

import { useState } from 'react';
import { Empty, Loading, Panel, useAct, useQuery } from '../books/ui.tsx';
import { momentText } from './model.ts';
import { requestWhat, requestWho, type MaterialRequest } from './requests-model.ts';
import './inventory.css';

export function Requests({ timeZone, toast }: { timeZone?: string | undefined; toast: (m: string) => void }) {
  const [all, setAll] = useState(false);
  const q = useQuery<{ requests: MaterialRequest[] }>('materialRequests.list', {
    status: all ? 'all' : 'open',
  });
  return (
    <Panel
      title="Asked for"
      aside={
        <button
          type="button"
          className={`btn quiet${all ? ' on' : ''}`}
          aria-pressed={all}
          onClick={() => setAll(!all)}
        >
          Show the answered ones too
        </button>
      }
    >
      {!q.data ? (
        <Loading error={q.error} what="what technicians asked for" retry={() => void q.reload()} />
      ) : q.data.requests.length ? (
        <ul className="count-lines material-requests">
          {q.data.requests.map((r) => (
            <li key={r.id}>
              <RequestRow request={r} timeZone={timeZone} toast={toast} onDone={() => q.reload()} />
            </li>
          ))}
        </ul>
      ) : (
        <Empty>
          {all
            ? 'Nobody has asked for anything yet.'
            : 'Nothing waiting. When a technician can’t find something on their truck, they ask here from the iPad.'}
        </Empty>
      )}
    </Panel>
  );
}

function RequestRow({
  request: r,
  timeZone,
  toast,
  onDone,
}: {
  request: MaterialRequest;
  timeZone?: string | undefined;
  toast: (m: string) => void;
  onDone: () => Promise<void>;
}) {
  const [answer, setAnswer] = useState<'got' | 'not_got' | null>(null);
  const [note, setNote] = useState('');
  const act = useAct(toast, onDone);
  const needsNote = answer === 'not_got' && !note.trim();
  return (
    <>
      <div className="count-name">
        <b>{requestWhat(r)}</b>
        {r.status !== 'open' && (
          <span className={`chip${r.status === 'got' ? ' ok' : ''}`}>
            {r.status === 'got' ? 'Got' : 'Not got'}
          </span>
        )}
      </div>
      <div className="count-figs">
        <span className="small">{requestWho(r)}</span>
        <span className="muted small">asked {momentText(r.requestedAt, timeZone)}</span>
        {r.note && <span className="muted small">“{r.note}”</span>}
        {r.handledNote && <span className="small">Said: {r.handledNote}</span>}
      </div>
      {r.status === 'open' &&
        (answer ? (
          <form
            className="row-actions"
            aria-label={`Answer ${r.staffName}`}
            onSubmit={(e) => {
              e.preventDefault();
              if (needsNote || act.busy) return;
              void act.run(
                'materialRequests.handle',
                { requestId: r.id, outcome: answer, ...(note.trim() ? { note: note.trim() } : {}) },
                answer === 'got' ? `Told ${r.staffName} it’s got.` : `Told ${r.staffName} it can’t be got.`,
              );
            }}
          >
            <label>
              <span className="flabel">
                {answer === 'got' ? `A note for ${r.staffName} (optional)` : `Why not, for ${r.staffName}`}
              </span>
              <input
                className="field"
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            <button type="submit" className="btn primary" disabled={needsNote || act.busy}>
              {answer === 'got' ? 'Mark it got' : 'Mark it not got'}
            </button>
            <button type="button" className="btn quiet" onClick={() => setAnswer(null)}>
              Back
            </button>
          </form>
        ) : (
          <div className="row-actions">
            <button type="button" className="btn" onClick={() => setAnswer('got')}>
              Got it…
            </button>
            <button type="button" className="btn quiet" onClick={() => setAnswer('not_got')}>
              Can’t get it…
            </button>
          </div>
        ))}
    </>
  );
}
